'use client';

import { Button } from '@ahafood/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@ahafood/ui/components/dialog';
import type { Address } from '@ahm/api-wrappers-core';
import { MapPin } from 'lucide-react';
import { useCallback, useEffect } from 'react';
import { useAddAddress } from '@/api/use-add-new-address';
import { useStoreCitySheet } from '@/contexts/store-city-sheet-context';
import { useLocationNudge } from '@/hooks/use-location-nudge';
import {
  detectCityFromCoordinates,
  detectCityFromAddressText,
  generatePlaceId,
} from '@/lib/fast-location-utils';
import { useDetectWebInApp } from '@/lib/is-webinapp/use-detect-webinapp';
import { useAiFoodOrderStore } from '@/store';
import { useAuthStore } from '@/store/use-auth-store';

const AUTO_DISMISS_MS = 10000;

interface LocationNudgeProps {
  detectedAddress: Address;
  onAccept: () => void;
  onDismiss: () => void;
}

function LocationNudge({
  detectedAddress,
  onAccept,
  onDismiss,
}: LocationNudgeProps) {
  const shortAddress =
    detectedAddress.address?.split(',').slice(0, 2).join(',') ??
    'vị trí hiện tại';

  useEffect(() => {
    const timer = setTimeout(onDismiss, AUTO_DISMISS_MS);
    return () => clearTimeout(timer);
  }, [onDismiss]);

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onDismiss();
      }}
    >
      <DialogContent className="max-w-[calc(100%-2rem)] rounded-2xl sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <MapPin className="size-5 flex-none text-primary-50" />
            Phát hiện vị trí mới
          </DialogTitle>
          <DialogDescription className="text-xs text-left text-neutral-60">
            Có vẻ bạn đang ở nơi khác với địa chỉ đang dùng. Cập nhật để đặt
            đúng địa chỉ nhé?
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-start gap-3 rounded-xl border border-primary-20 bg-primary-5 p-3">
          <MapPin className="mt-0.5 size-4 flex-none text-primary-50" />
          <p className="text-sm font-semibold text-neutral-80">
            {shortAddress}
          </p>
        </div>
        <DialogFooter className="flex-row gap-2 sm:gap-2">
          <Button
            variant="outline"
            className="flex-1 font-bold"
            onClick={onDismiss}
            type="button"
          >
            Bỏ qua
          </Button>
          <Button
            variant="default"
            className="flex-1 font-bold"
            onClick={onAccept}
            type="button"
          >
            Cập nhật ngay
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function HomeLocationNudgeContainer() {
  const { showCityPopup } = useStoreCitySheet();
  const address = useAiFoodOrderStore.use.address();
  const setAddress = useAiFoodOrderStore.use.setAddress();
  const setCityId = useAiFoodOrderStore.use.setCityId();
  const setSkipAutoLocation = useAiFoodOrderStore.use.setSkipAutoLocation();
  const isWebinapp = useDetectWebInApp();

  const user = useAuthStore((state) => state.user);
  const systemToken = user?.systemToken;
  const { mutate: addAddress } = useAddAddress(systemToken);

  const { showNudge, detectedAddress, dismissNudge, acceptNudge } =
    useLocationNudge(address ?? null);

  const handleAccept = useCallback(() => {
    const newAddress = acceptNudge();
    if (!newAddress) return;

    const addressWithPlaceId = {
      ...newAddress,
      place_id:
        newAddress.place_id ??
        generatePlaceId(newAddress.lat!, newAddress.lng!),
    };

    // Detect and set city synchronously (mirrors StoreLocation behavior)
    if (addressWithPlaceId.lat && addressWithPlaceId.lng) {
      const city = detectCityFromCoordinates(
        addressWithPlaceId.lat,
        addressWithPlaceId.lng
      );
      if (city) {
        setCityId(city.id);
      }
    } else if (addressWithPlaceId.address) {
      const textCity = detectCityFromAddressText(addressWithPlaceId.address);
      if (textCity) setCityId(textCity.value);
    }

    // Update address immediately (sync) — triggers React Query key change
    setAddress(addressWithPlaceId);
    setSkipAutoLocation(false);

    // Mode-specific behavior
    if (!isWebinapp) {
      // Native web: reload immediately for fresh SSR (don't wait for server save)
      setTimeout(() => window.location.reload(), 100);
    }
    // WebInApp: React Query automatically refetches via new geohash key — no explicit invalidation needed

    // Save to server in background (fire-and-forget, non-blocking)
    if (
      systemToken &&
      addressWithPlaceId.lat &&
      addressWithPlaceId.lng &&
      addressWithPlaceId.address
    ) {
      addAddress({
        address: addressWithPlaceId.address,
        place_id: addressWithPlaceId.place_id,
        lat: addressWithPlaceId.lat,
        lng: addressWithPlaceId.lng,
      });
    }
  }, [
    acceptNudge,
    setAddress,
    setCityId,
    setSkipAutoLocation,
    isWebinapp,
    systemToken,
    addAddress,
  ]);

  if (!showNudge || !detectedAddress || showCityPopup) return null;

  return (
    <LocationNudge
      detectedAddress={detectedAddress}
      onAccept={handleAccept}
      onDismiss={dismissNudge}
    />
  );
}
